// supervisor-outcome-router-needs-decision-path.ts
//
// Nhánh `needs_decision` của supervisor-outcome-router.ts, tách ra theo đúng
// khuôn `*-done-path.ts` để router giữ ngân sách 200 dòng.
//
// B4: worker khai `needs_decision` ⇒ ghi câu hỏi interview, KHÔNG tính
// counted_failure — cần NGƯỜI quyết, không phải lỗi của worker.
//
// V2-0 (spec V2 §4.0): id câu hỏi theo nội dung. Worker hỏi lại ĐÚNG câu người
// đã trả lời (cùng hash ⇒ cùng id) ⇒ 1 counted failure `contract_error`, không
// đẻ câu mới: câu trả lời đã nằm trong prompt (mục "Quyết định đã chốt cho
// ticket này"), hỏi lại là worker bỏ qua hợp đồng chứ không phải cần người.
// Câu khác hash ⇒ câu required mới, KHÔNG tính failure.
// Category `contract_error` này cố ý KHÔNG vào breaker "≥ 2 ticket dừng run"
// (contract-error-circuit-breaker.ts header): lỗi một worker, không lỗi hệ thống.

import type { DecisionAutoRunConfig } from "./decision-auto-boundary-tokens.js";
import { tagStopDecision } from "./escalation-blocked-by.js";
import { readInterview } from "./interview-store.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import type { MissionStore } from "./mission-store.js";
import { evaluateNeedsDecisionAuto } from "./needs-decision-auto-decision-evaluation.js";
import {
  buildNeedsDecisionQuestion,
  redactInterviewQuestionKnownValues,
} from "./needs-decision-interview-questions.js";
import { recordNeedsDecisionOutcome } from "./needs-decision-outcome-recording.js";
import { isNeedsDecisionQuestionOfTicket } from "./needs-decision-question-ticket-match.js";
import {
  type RetryConfig,
  type RetryDecision,
  type RetryState,
  recordAttemptOutcome,
} from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";

export interface NeedsDecisionPathInput {
  cycle: AttemptCycleResult;
  controlRoot: string;
  ticketId: string;
  now: Date;
  retryState: RetryState;
  retryConfig: RetryConfig;
  /** V2-1: journal của mission (E6 `decision_question_asked`, auto applied/skipped). */
  store: MissionStore;
  /** V2-1: worktree mission — nơi hash lại BLUEPRINT + đọc env.example/profile. */
  repoDir: string;
  /** V2-1: `ticket.kind` (luật auto điều 3). */
  ticketKind: string;
  /** V2-1: trần + token config của run, chụp lúc khởi động (vắng ⇒ không xét auto). */
  decisionAuto?: DecisionAutoRunConfig;
}

export type NeedsDecisionPathResult =
  | { kind: "needs_decision"; questionId: string; autoApplied?: true }
  | { kind: "retry_decision"; decision: RetryDecision };

/**
 * `undefined` khi attempt không phải needs_decision (đường cũ nguyên vẹn).
 *
 * Ticket quay về "todo" qua nhánh `undefined` của `journalAttemptOutcome`
 * (supervisor-loop-outcome-journal.ts:147-158) và gate `interview_unanswered`
 * giữ dispatch kế tiếp — worker chạy đúng một lần rồi chờ người.
 */
export async function routeNeedsDecisionOutcome(
  input: NeedsDecisionPathInput,
): Promise<NeedsDecisionPathResult | undefined> {
  if (input.cycle.kind !== "verified") return undefined;
  const built = buildNeedsDecisionQuestion(input.cycle.sessionResult.result, input.ticketId);
  if (built === undefined) return undefined;
  // Phase 04a: id tính trên text thô (phase 01) rồi mới che text (tầng hai).
  const knownValues = await knownValuesForControlRoot(input.controlRoot);
  const question = redactInterviewQuestionKnownValues(built, knownValues);

  const existing = await readInterview(input.controlRoot);
  if (existing?.answers[question.id] !== undefined) {
    return {
      kind: "retry_decision",
      // N16: a repeated needs_decision at the ceiling is never recovery-eligible.
      decision: tagStopDecision(
        recordAttemptOutcome(
          input.retryState,
          {
            category: "contract_error",
            evidence:
              `needs_decision lặp đúng câu đã được người trả lời (${question.id}) — ` +
              'quyết định đã có trong mục "Quyết định đã chốt cho ticket này" của prompt.',
          },
          input.retryConfig,
          input.now,
        ),
        "other",
      ),
    };
  }

  // writeInterviewQuestions ghi đè cả bộ, nên phải gộp thủ công: giữ câu của
  // ticket KHÁC và câu ĐÃ TRẢ LỜI của chính ticket này (lịch sử, không chặn
  // dispatch). Chỉ câu CHƯA trả lời của ticket này bị thay bằng câu mới (chốt
  // 06/10): nó đã lỗi thời (worker vừa hỏi câu khác), giữ lại sẽ chặn ticket
  // mãi bằng một câu không ai còn hỏi. Ca này gần như chỉ xảy ra khi gate đã
  // cho qua dù còn câu treo; directive answer cho câu bị thay (nếu còn trong
  // inbox) bị từ chối có lý do, không mất im lặng.
  const others = (existing?.questions ?? []).filter(
    (q) =>
      !isNeedsDecisionQuestionOfTicket(q, input.ticketId) || existing?.answers[q.id] !== undefined,
  );

  // V2-1 (§4.1): xét auto TRƯỚC khi ghi câu required — đủ 5 điều kiện thì câu
  // được ghi kèm câu trả lời `delegated:<G0 id>` (không chặn dispatch), ticket
  // chạy lại với mục "Quyết định đã chốt". Không thì câu required như V2-0.
  const result = input.cycle.sessionResult.result as Record<string, unknown>;
  const auto = await evaluateNeedsDecisionAuto(
    {
      controlRoot: input.controlRoot,
      repoDir: input.repoDir,
      ticketKind: input.ticketKind,
      runConfig: input.decisionAuto,
    },
    result,
  );
  const autoApplied = await recordNeedsDecisionOutcome({
    controlRoot: input.controlRoot,
    store: input.store,
    question,
    others,
    auto,
    recommendedOptionId:
      typeof result.recommended_option_id === "string" ? result.recommended_option_id : undefined,
    now: input.now,
    knownValues,
  });

  return {
    kind: "needs_decision",
    questionId: question.id,
    ...(autoApplied ? { autoApplied: true as const } : {}),
  };
}
